import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { durationUnits } from "../durationUnits";
import { formatDurationLabel, parseLocalYmd, weekdayNames, type Plan, type Session, formatDayMonth } from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import type { PlansResourceStatus } from "../Plans/usePlans";
import { Button } from "../primitives/Button";
import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { IconButton } from "../primitives/IconButton";
import { useLocale } from "../primitives/useLocale";
import { webTapFloor, type UiTheme } from "../theme";
import { useDateLocale } from "../primitives/DateFormatContext";

// #368: the plans of the day tapped on the diary's calendar. A row opens the
// plan in its editor; Start, where the host has a logger, starts it, as the
// Library's plan card does. Under the grid on the phone, in the detail pane
// on the wide web.
// #374: and the workouts done that day, a row opens its summary. A plan of a
// past day not done says so, with Plan again in place of Start. The + in the
// heading plans a workout on the day, today or later.
export function PlannedDayList({
  theme,
  day,
  plans,
  sessions,
  missed,
  status,
  ready,
  onOpenPlan,
  onStartPlan,
  startingPlanId,
  onOpenSession,
  onPlanOn,
  onPlanAgain,
}: {
  theme: UiTheme;
  /** "YYYY-MM-DD". */
  day: string;
  /** That day's plans. */
  plans: readonly Plan[];
  /** That day's workouts, done. */
  sessions: readonly Session[];
  /** The ids of the plans not done. */
  missed: ReadonlySet<string>;
  status: PlansResourceStatus;
  /** false: the unit is not known yet, + and Plan again wait. */
  ready: boolean;
  onOpenPlan: (plan: Plan) => void;
  onStartPlan?: (plan: Plan) => void;
  startingPlanId?: string | null;
  onOpenSession: (session: Session) => void;
  /** Absent: a past day, no +. */
  onPlanOn?: () => void;
  onPlanAgain: (plan: Plan) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dateLocale = useDateLocale();
  const styles = makeStyles(theme);
  const date = parseLocalYmd(day);
  const when = date ? `${weekdayNames(locale, 0)[date.getDay()]} ${formatDayMonth(date, dateLocale)}` : day;
  const counts = [
    ...(plans.length > 0 ? [t("calendar.planned", { count: plans.length })] : []),
    ...(sessions.length > 0 ? [t("calendar.done", { count: sessions.length })] : []),
  ];
  const icon = theme.typography.sizes.md;

  const planRow = (plan: Plan) => {
    const notDone = missed.has(plan.id);
    return (
      <View key={plan.id} style={styles.row}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={notDone ? `${plan.name}, ${t("calendar.missed")}` : plan.name}
          accessibilityHint={t("calendar.openPlanHint")}
          onPress={() => onOpenPlan(plan)}
          style={({ pressed }) => [styles.main, styles.planMain, pressed && styles.pressed]}
        >
          <View style={styles.flex}>
            <Text style={styles.name} numberOfLines={2}>
              {plan.name}
            </Text>
            <Text style={styles.muted}>
              {notDone ? <Text style={styles.missed}>{`${t("calendar.missed")} · `}</Text> : null}
              {t("calendar.exercises", { count: plan.entries.length })}
            </Text>
          </View>
          <Icon name="chevron-right" size={icon} color={theme.palette.ink3} />
        </Pressable>
        {notDone ? (
          <Button
            theme={theme}
            variant="secondary"
            compact
            icon="calendar"
            label={t("mobile.workouts.planAgain")}
            accessibilityLabel={`${t("mobile.workouts.planAgain")}, ${plan.name}`}
            onPress={() => onPlanAgain(plan)}
            disabled={!ready}
          />
        ) : onStartPlan ? (
          <Button
            theme={theme}
            variant="secondary"
            compact
            icon="play"
            label={t("plans.start")}
            accessibilityLabel={`${t("plans.start")}, ${plan.name}`}
            onPress={() => onStartPlan(plan)}
            loading={startingPlanId === plan.id}
            disabled={!!startingPlanId && startingPlanId !== plan.id}
          />
        ) : null}
      </View>
    );
  };

  const sessionRow = (s: Session) => {
    const name = s.name ?? t("mobile.workouts.fallbackName");
    const duration = formatDurationLabel(s.startedAt, s.endedAt, durationUnits(t));
    return (
      <Pressable
        key={s.id}
        accessibilityRole="button"
        accessibilityLabel={duration ? `${name}, ${duration}` : name}
        accessibilityHint={t("calendar.openSessionHint")}
        onPress={() => onOpenSession(s)}
        style={({ pressed }) => [styles.row, styles.main, styles.sessionRow, pressed && styles.pressed]}
      >
        <View {...hiddenFromScreenReader()}>
          <Icon name="check" size={icon} color={theme.palette.ink2} />
        </View>
        <View style={styles.flex}>
          <Text style={styles.name} numberOfLines={2}>
            {name}
          </Text>
          {duration ? <Text style={styles.muted}>{duration}</Text> : null}
        </View>
        <Icon name="chevron-right" size={icon} color={theme.palette.ink3} />
      </Pressable>
    );
  };

  const plansBody =
    status === "loading" ? (
      <ActivityIndicator color={theme.palette.ink2} accessibilityLabel={t("plans.loading")} />
    ) : status === "error" && plans.length === 0 ? (
      <Text style={styles.muted}>{t("calendar.loadError")}</Text>
    ) : plans.length > 0 ? (
      <>
        <Text style={styles.section} accessibilityRole="header">
          {t("calendar.plannedHeading")}
        </Text>
        {plans.map(planRow)}
      </>
    ) : sessions.length === 0 ? (
      <Text style={styles.muted}>{t("calendar.nothing")}</Text>
    ) : null;

  return (
    <View style={styles.wrap}>
      <View style={styles.headRow}>
        <Text style={styles.head} accessibilityRole="header">
          {[when, ...counts].join(" · ")}
        </Text>
        {onPlanOn ? (
          <IconButton
            theme={theme}
            name="plus"
            accessibilityLabel={t("calendar.planOn", { date: when })}
            onPress={onPlanOn}
            disabled={!ready}
          />
        ) : null}
      </View>
      {plansBody}
      {sessions.length > 0 ? (
        <>
          <Text style={styles.section} accessibilityRole="header">
            {t("calendar.doneHeading")}
          </Text>
          {sessions.map(sessionRow)}
        </>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2, padding: s.s4 },
    headRow: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    head: { flex: 1, color: p.ink, fontSize: ty.sizes.base, fontWeight: "700" },
    section: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "700", paddingTop: s.s2 },
    row: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: s.s3,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      paddingRight: s.s3,
    },
    main: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      minHeight: webTapFloor ?? 44,
      paddingVertical: s.s3,
      paddingLeft: s.s3,
    },
    // The plan's open area wraps the action button under it when it does not fit (large font, long labels).
    planMain: { flexGrow: 1, flexShrink: 1, flexBasis: 160 },
    // A whole row is the button: it does not grow in the column.
    sessionRow: { flex: 0 },
    pressed: { opacity: 0.6 },
    flex: { flex: 1, gap: 2 },
    name: { color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    muted: { color: p.ink3, fontSize: ty.sizes.sm },
    missed: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "700" },
  });
}
